import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  useMediaQuery,
  useTheme,
  Chip
} from '@mui/material';
import { Menu as MenuIcon, AdminPanelSettings, Logout } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { currentUser, isAdmin, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    if (path === '/') {
      // If navigating to home page, scroll to yachts section if already on home
      if (location.pathname === '/') {
        const yachtsSection = document.getElementById('yachts-section');
        if (yachtsSection) {
          yachtsSection.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
          setMobileOpen(false);
          return;
        }
      }
    }
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const menuItems = [
    { label: 'Jachty', path: '/' },
    { label: 'O nas', path: '/about' },
    { label: 'Kontakt', path: '/contact' }
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: 2 }}>
        <Box
          component="img"
          src="/images/yachts/logo/logo.png.png"
          alt="Madacar Yacht Logo"
          sx={{
            height: 40,
            width: 'auto',
            mr: 1
          }}
        />
        <Typography variant="h6" sx={{ color: '#1976d2' }}>
          Madacar Yacht Czarter
        </Typography>
      </Box>
      
      {/* Admin Status - only for logged in admins */}
      {currentUser && isAdmin && (
        <Box sx={{ mb: 2, px: 2 }}>
          <Chip
            label="Administrator"
            color="primary"
            variant="outlined"
            size="small"
          />
        </Box>
      )}
      
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.label} disablePadding>
            <Button
              fullWidth
              onClick={() => handleNavigation(item.path)}
              sx={{
                color: location.pathname === item.path ? '#1976d2' : '#666',
                fontWeight: location.pathname === item.path ? 600 : 400,
                py: 2
              }}
            >
              {item.label}
            </Button>
          </ListItem>
        ))}
        
        {/* Admin Panel in Mobile */}
        {currentUser && isAdmin && (
          <ListItem disablePadding>
            <Button
              fullWidth
              onClick={() => handleNavigation('/admin')}
              startIcon={<AdminPanelSettings />}
              sx={{
                color: location.pathname === '/admin' ? '#1976d2' : '#666',
                fontWeight: location.pathname === '/admin' ? 600 : 400,
                py: 2,
                backgroundColor: location.pathname === '/admin' ? '#e3f2fd' : 'transparent'
              }}
            >
              Panel Admina
            </Button>
          </ListItem>
        )}
        
        {/* Admin Logout in Mobile - only for logged in admins */}
        {currentUser && isAdmin && (
          <ListItem disablePadding>
            <Button
              fullWidth
              onClick={handleLogout}
              startIcon={<Logout />}
              sx={{
                color: '#d32f2f',
                py: 2,
                '&:hover': {
                  backgroundColor: '#ffebee'
                }
              }}
            >
              Wyloguj
            </Button>
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        sx={{ 
          backgroundColor: 'white',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Logo */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': { opacity: 0.8 }
            }}
            onClick={() => navigate('/')}
          >
            <Box
              component="img"
              src="/images/yachts/logo/logo.png.png"
              alt="Madacar Yacht Logo"
              sx={{
                height: { xs: 40, md: 50 },
                width: 'auto',
                mr: 1
              }}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#1976d2', 
                fontWeight: 700,
                fontSize: { xs: '1.1rem', md: '1.25rem' }
              }}
            >
              Madacar Yacht Czarter
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.label}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    color: location.pathname === item.path ? '#1976d2' : '#666',
                    fontWeight: location.pathname === item.path ? 600 : 400,
                    textTransform: 'none',
                    fontSize: '1rem',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      color: '#1976d2'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
              
              {/* Admin Panel Button */}
              {currentUser && isAdmin && (
                <Button
                  onClick={() => handleNavigation('/admin')}
                  startIcon={<AdminPanelSettings />}
                  sx={{
                    color: location.pathname === '/admin' ? '#1976d2' : '#666',
                    fontWeight: location.pathname === '/admin' ? 600 : 400,
                    textTransform: 'none',
                    fontSize: '1rem',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: location.pathname === '/admin' ? '#e3f2fd' : 'transparent',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2'
                    }
                  }}
                >
                  Panel Admina
                </Button>
              )}
              
              {/* Admin Logout Button - only for logged in admins */}
              {currentUser && isAdmin && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                  <Chip
                    label="Admin"
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Button
                    onClick={handleLogout}
                    startIcon={<Logout />}
                    sx={{
                      color: '#666',
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      px: 2,
                      py: 0.5,
                      borderRadius: 2,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: '#ffebee',
                        color: '#d32f2f'
                      }
                    }}
                  >
                    Wyloguj
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: '#1976d2' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 240,
            pt: 2
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
